import React from "react";
import { StyleSheet, ScrollView, View, Pressable, Image, Dimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FindifyTopBarOverlay } from "../genericPages/components/FindifyTopBarOverlay";
import { useContext, useEffect, useState } from "react";
import { FindifyDialog } from "../genericPages/components/FindifyDialog";
import { BasePage } from "../genericPages/helpers/BasePage";
import { useTranslation } from "react-i18next";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { FindifySimpleHeader, SimpleHeaderAction } from "../genericPages/components/FindifySimpleHeader";
import { PlaceColors } from "../genericPages/helpers/PlaceColors";
import { IconShadow, TextShadow, DefaultShadow } from "../genericPages/helpers/DefaultShadows";
import { BottomNavigationPadding } from "../genericPages/components/FindifyNavigation";
import { getTranslatedString } from "../genericPages/helpers/FindifyTranslatedString";
import { sharePlace, toggleFavouriteWorkspace } from "@/layouts/genericPages/helpers/HompageHelpers";
import { getApiFromModule } from "@/layouts/genericPages/helpers/GetContentsByModule";
import { PlaceText } from "../genericPages/components/PlaceText";
import { FlatList } from "react-native-gesture-handler";
import { MakeContentsLine } from "../genericPages/helpers/ContentSlider";
import { getFeaturedImageUrl } from "../genericPages/helpers/DetailsHelpers";
import { getWorkspaceDescription, getWorkspaceTitle } from "../genericPages/helpers/WorkspaceText";


import { FindifyCMSPage } from "../genericPages/FindifyCMSPage";


type PlacePageHeaderArgs = {
    navigation: any,
    enableDonations: boolean,
    enableSharing: boolean,
    enableDownload: boolean,
    enableFavorites: boolean,
    findifyDataContext: FindifyDataContextType | null,
}
export const PlacePageHeader = ({ navigation,
    enableDonations,
    enableSharing,
    enableDownload,
    enableFavorites,
    findifyDataContext,
}: PlacePageHeaderArgs) => {
    const [dialogCode, setDialogCode] = useState<JSX.Element | null>(null);
    const { t } = useTranslation();
    const [actions, setActions] = useState<SimpleHeaderAction[]>([]);

    useEffect(() => {
        const actionsList: SimpleHeaderAction[] = [];
        if (enableSharing) {
            actionsList.push({
                icon: 'share-outline',
                action: () => sharePlace(findifyDataContext)
            })
        }
        if (enableFavorites) {
            const alreadyFavourite = findifyDataContext?.favouriteWorkspaces.includes(findifyDataContext?.workspace?.workspace_id);
            actionsList.push({
                color: alreadyFavourite ? "red" : undefined,
                icon: alreadyFavourite ? 'heart' : 'heart-outline',
                action: () => toggleFavouriteWorkspace(findifyDataContext)
            });
        }
        if (enableDownload) {
            actionsList.push({
                icon: 'download-outline',
                action: () => {
                    setDialogCode(<FindifyDialog
                        onAction1={() => { console.log('action1'); setDialogCode(null) }}
                        onAction2={() => { console.log('action2'); setDialogCode(null) }}
                        action1Text={t('popup.yes')}
                        action2Text={t('popup.no')}
                        message="download outline" />);
                }
            });
        }
        if (enableDonations) {
            actionsList.push({
                icon: 'logo-euro',
                action: () => {
                    setDialogCode(<FindifyDialog
                        onAction1={() => { console.log('action1'); setDialogCode(null) }}
                        onAction2={() => { console.log('action2'); setDialogCode(null) }}
                        action1Text={t('popup.yes')}
                        action2Text={t('popup.no')}
                        message="logo outline" />);
                }
            });
        }
        setActions(actionsList);

    }, [findifyDataContext])

    return <FindifySimpleHeader
        navigation={navigation}
        hideGoBack={true}
        image={findifyDataContext?.workspace?.app_settings?.app_banner_url}
        actions={actions}
        title={getWorkspaceTitle(findifyDataContext)}
        subtitle={getWorkspaceDescription(findifyDataContext)}
        changePlaceLeft={true}
    >

        <Pressable
            onPress={() => {
                navigation.push('PlaceInfo', {
                    // module: 'content',
                    // content_id: content.content_id,
                    // page_slug: content.page_slug
                });
            }}
            style={[{
                backgroundColor: PlaceColors(findifyDataContext).primary.get(),
                paddingVertical: 10,
                paddingHorizontal: 20,
                borderRadius: 24,
                marginTop: -28, // Overlaps the curved header cleanly
                marginRight: 20,
                marginLeft: 'auto'
            }, DefaultShadow]}
        >
            <PlaceText style={{ fontWeight: '600', fontSize: 16, color: PlaceColors(findifyDataContext).background.get() }}>{t('base.know_more')}</PlaceText>
        </Pressable>
    </FindifySimpleHeader>

}


export const loadContentsLines = async ({
    t,
    findifyDataContext,
    setContentsLines,
    navigation,
    limit }: {
        t: any,
        findifyDataContext: FindifyDataContextType,
        setContentsLines: Function,
        navigation: any,
        limit: number
    }) => {
    const contentsToAdd: any = {};
    let validLines = 0;
    findifyDataContext?.workspace?.app_settings?.homepage_lines?.map((line: any, index: number) => {
        if (line.module && line.module !== '') {
            validLines++;
            const api = getApiFromModule(line.module, findifyDataContext);
            let orderby = {};
            let filters = {};
            let lineCat: any = undefined;
            if (line.category && line.category !== '') {
                filters = { 'categories.category.category_id': +line.category }
            }

            api?.getList(0, limit, null, filters, orderby).then(async (res: any) => {
                const lineItems = res.results.map((item: any) => {
                    lineCat = item.categories.find((cat: any) => cat.category_id === +line.category)?.category;
                    return {
                        ...item,
                        image: getFeaturedImageUrl(item),
                        action: () => {
                            navigation.push('PlaceContentDetails', {
                                module: line.module,
                                content_id: item.content_id,
                                page_slug: item.page_slug
                            })
                        }
                    }
                });
                if (lineCat === undefined) {
                    const catApi = getApiFromModule('categories', findifyDataContext);
                    await catApi.getList(0, 1, null, {
                        category_id_in: JSON.stringify([+line.category])
                    }, null).then((cat: any) => {
                        if (cat.results.length > 0) {
                            lineCat = cat.results[0];
                        }
                    })
                }

                contentsToAdd[index] = {
                    ...line,
                    title: (lineCat?.name) ? getTranslatedString(lineCat?.name, findifyDataContext) : t('modules.content_types.' + line.module),
                    category: lineCat,
                    items: lineItems,
                }

                if (Object.keys(contentsToAdd).length === validLines) {
                    setContentsLines(Object.keys(contentsToAdd).map((key: string) => contentsToAdd[key]));
                }
            })
        }
    })
}

export const PlaceTab = ({ navigation }: any) => {
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    const findifyDataContext = useContext(FindifyDataContext);
    const [contentsLines, setContentsLines] = useState<any[]>([]);

    useEffect(() => {
        loadContentsLines({ t, findifyDataContext, setContentsLines, navigation, limit: 99 });
    }, []);

    return <BasePage navigation={navigation}>
        <FindifyTopBarOverlay />
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={{ paddingBottom: insets.bottom + BottomNavigationPadding }}>
            <View style={{
                paddingBottom: insets.bottom + BottomNavigationPadding,
                backgroundColor: PlaceColors(findifyDataContext).background.get(),

            }}>
                <PlacePageHeader
                    enableDonations={false}
                    enableSharing={true}
                    enableDownload={false}
                    enableFavorites={true}
                    navigation={navigation}
                    findifyDataContext={findifyDataContext}
                />
                {findifyDataContext?.workspace?.app_settings?.homepage_id ? (
                    <FindifyCMSPage 
                        pageId={findifyDataContext.workspace.app_settings.homepage_id} 
                        navigation={navigation} 
                    />
                ) : (
                    contentsLines.map((line: any, index: number) => {
                        const cat = line?.category;
                        const contents = line?.items;
                        switch (index) {
                            case 0:
                                const content = contents[0];
                                return <View key={'content_line' + index} style={[{
                                    margin: 15,
                                    padding: 15,
                                    backgroundColor: PlaceColors(findifyDataContext).background.get(),
                                    borderRadius: 24,
                                }, DefaultShadow]}>
                                    <View style={{
                                        display: 'flex',
                                        width: '100%',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginBottom: 16,
                                    }}>
                                        <PlaceText style={{
                                            color: PlaceColors(findifyDataContext).primary.get(),
                                            fontSize: 28,
                                            fontWeight: '800',
                                            textTransform: 'uppercase',
                                        }}>
                                            {getTranslatedString(cat?.name, findifyDataContext)}
                                        </PlaceText>
                                        <PlaceText style={{
                                            color: PlaceColors(findifyDataContext).text.get(),
                                            fontSize: 14,
                                            textAlign: 'center',
                                            marginTop: 4,
                                        }}>
                                            {getTranslatedString(cat?.description, findifyDataContext)}
                                        </PlaceText>
                                    </View>
                                    <View style={{
                                        width: '100%',
                                        gap: 15,
                                        display: 'flex',
                                        flexDirection: 'row'
                                    }}>
                                        <Image
                                            style={{
                                                flex: 1,
                                                width: '100%',
                                                objectFit: 'cover',
                                                aspectRatio: 1,
                                                borderRadius: 16
                                            }}
                                            source={{ uri: getFeaturedImageUrl(content) }} />
                                        <View style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between'
                                        }}>
                                            <View>
                                                <PlaceText style={{
                                                    color: PlaceColors(findifyDataContext).text.get(),
                                                    fontWeight: 'bold',
                                                    fontSize: 16,
                                                    marginBottom: 4,
                                                }}>{getTranslatedString(content?.name, findifyDataContext)}</PlaceText>
                                                <PlaceText style={{
                                                    color: PlaceColors(findifyDataContext).text.get(),
                                                    opacity: 0.8,
                                                }} numberOfLines={4}>{getTranslatedString(content?.resume, findifyDataContext)}</PlaceText>
                                            </View>
                                            <Pressable
                                                onPress={() => {
                                                    navigation.push('PlaceContentDetails', {
                                                        module: 'contents',
                                                        content_id: content.content_id,
                                                        page_slug: content.page_slug
                                                    });
                                                }}
                                                style={[{
                                                    backgroundColor: PlaceColors(findifyDataContext).primary.get(),
                                                    paddingVertical: 8,
                                                    paddingHorizontal: 16,
                                                    borderRadius: 20,
                                                    marginLeft: 'auto',
                                                    marginTop: 10,
                                                }]}
                                            >
                                                <PlaceText style={{ color: PlaceColors(findifyDataContext).background.get(), fontWeight: '600' }}>{t('base.read_more')}</PlaceText>
                                            </Pressable>
                                        </View>
                                    </View>
                                </View>;
                            case 1:
                                return <View key="timeline-container" style={{ marginVertical: 10 }}>
                                    <FlatList
                                        horizontal
                                        contentContainerStyle={{
                                            paddingHorizontal: 15,
                                            gap: 15,
                                            paddingVertical: 10,
                                        }}
                                        showsHorizontalScrollIndicator={false}
                                        showsVerticalScrollIndicator={false}
                                        data={[...contents].reverse()}
                                        renderItem={({ item, index }) => {
                                            return <Pressable onPress={() => {
                                                navigation.push('PlaceContentDetails', {
                                                    module: line.module,
                                                    content_id: item.event_id,
                                                    page_slug: item.page_slug
                                                });
                                            }}>
                                                <View style={[{
                                                    backgroundColor: PlaceColors(findifyDataContext).background.get(),
                                                    borderRadius: 16,
                                                    padding: 15,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 8,
                                                    width: Dimensions.get('window').width * 0.45,
                                                    alignContent: 'center',
                                                    alignItems: 'center',
                                                }, DefaultShadow]}>
                                                    <View style={{ width: 12, height: 12, backgroundColor: PlaceColors(findifyDataContext).primary.get(), borderRadius: 6, marginBottom: 4 }} />
                                                    <PlaceText style={{ textAlign: 'center', color: PlaceColors(findifyDataContext).primary.get(), fontWeight: 'bold', fontSize: 16 }}>{getTranslatedString(item?.name, findifyDataContext)}</PlaceText>
                                                    <PlaceText style={{ textAlign: 'center', color: PlaceColors(findifyDataContext).text.get(), fontSize: 13, opacity: 0.8 }} numberOfLines={3}>{getTranslatedString(item?.resume, findifyDataContext)}</PlaceText>
                                                </View>
                                            </Pressable>
                                        }}
                                    />
                                </View>;
                            case (contentsLines.length - 1):
                                if (contentsLines.length - 1 > 1) { // Prevent duplicates
                                    return <Pressable
                                        onPress={() => {
                                            navigation.push('PlaceContentDetails', {
                                                module: line.module,
                                                content_id: contents[0].content_id,
                                                page_slug: contents[0].page_slug
                                            });
                                        }}
                                    >
                                        <View
                                            style={[{
                                                height: 150,
                                                marginHorizontal: 15,
                                                marginVertical: 10,
                                                borderRadius: 24,
                                                overflow: 'hidden',
                                                backgroundColor: PlaceColors(findifyDataContext).background.get(),
                                            }, DefaultShadow]}
                                        >
                                            <Image
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    position: 'absolute',
                                                }}
                                                source={{ uri: getFeaturedImageUrl(contents[0]) }} />
                                            {/* Gradient/Dark Overlay */}
                                            <View style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.4)' }} />
                                            <View
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    alignContent: 'center',
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    paddingHorizontal: 20,
                                                }}
                                            >
                                                <PlaceText style={[TextShadow, {
                                                    textAlign: 'center',
                                                    color: "white",
                                                    fontWeight: 'bold',
                                                    fontSize: 22,
                                                }]}>{getTranslatedString(contents[0]?.resume, findifyDataContext)}</PlaceText>
                                            </View>
                                        </View>
                                    </Pressable>
                                }
                                break;
                            default:
                                // Line as other modules homepages
                                return <MakeContentsLine
                                    key={"contents_line_" + index}
                                    language={findifyDataContext?.language ?? ''}
                                    title={line.title}
                                    lIndex={+index}
                                    contents={line.items}
                                    module={line.module}
                                    viewAllAction={() => {
                                        navigation.push('PlaceContentList', findifyDataContext?.workspace.app_settings?.homepage_lines[index])
                                    }}
                                />;
                        }
                    })
                )}
                </View>
        </ScrollView>
    </BasePage>;
};


const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    headerIcon: {
        padding: 5,
        ...IconShadow,
    }
});
